import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Plus,
  User,
  SlidersHorizontal,
  Copy,
  RefreshCw,
  ShieldAlert,
  Building2,
} from "lucide-react";
import {
  listPlatformUsers,
  createPlatformUser,
  listTenants,
  type CurrentUser,
  type PlatformUserItem,
  type Tenant,
} from "../../lib/api";

export function TeamManagementView({ token, user }: { token: string; user: CurrentUser }) {
  const queryClient = useQueryClient();
  const isSuperAdmin = user.role === "superadmin" || user.role === "platform_admin";

  const [searchQuery, setSearchQuery] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedTenantFilter, setSelectedTenantFilter] = useState("all");
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);

  // Form states
  const [newUsername, setNewUsername] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [newUserRole, setNewUserRole] = useState<"admin" | "tenant">("tenant");
  const [selectedTenantId, setSelectedTenantId] = useState<string>(user.tenant_id || "");

  // Live Users Query from Backend
  const usersQuery = useQuery({
    queryKey: ["platform-users", token],
    queryFn: () => listPlatformUsers(token),
    refetchInterval: 5_000,
  });

  // Live Tenants Query
  const tenantsQuery = useQuery({
    queryKey: ["tenants", "list"],
    queryFn: () => listTenants(token),
  });

  const users = usersQuery.data || [];
  const tenants = tenantsQuery.data || [];

  // Create User Mutation
  const createUserMutation = useMutation({
    mutationFn: () =>
      createPlatformUser(token, {
        username: newUsername.trim(),
        password: newPassword.trim(),
        role: newUserRole,
        tenant_id: newUserRole === "tenant" ? selectedTenantId || undefined : undefined,
        is_active: true,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["platform-users"] });
      setIsAddModalOpen(false);
      setNewUsername("");
      setNewPassword("");
      setNewUserRole("tenant");
    },
    onError: (err: any) => {
      alert(`Failed to create user: ${err.message || "Unknown error"}`);
    },
  });

  const filteredUsers = users.filter((u) => {
    const matchesSearch =
      u.username.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (u.tenant_name || "").toLowerCase().includes(searchQuery.toLowerCase());
    const matchesRole = roleFilter === "all" || u.role === roleFilter;
    const matchesStatus =
      statusFilter === "all" ||
      (statusFilter === "Active" && u.is_active) ||
      (statusFilter === "Suspended" && !u.is_active);
    const matchesTenant =
      selectedTenantFilter === "all" || u.tenant_id === selectedTenantFilter;
    return matchesSearch && matchesRole && matchesStatus && matchesTenant;
  });

  function copyText(text: string) {
    navigator.clipboard.writeText(text);
  }

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight flex items-center gap-2">
            <User className="w-6 h-6 text-emerald-600" />
            Users Management
          </h1>
          <p className="text-xs text-slate-500 mt-1">
            System accounts classified strictly into two types: <strong>Admin</strong> (Platform Control) and <strong>Tenant</strong> (Organization Operations).
          </p>
        </div>

        {isSuperAdmin && (
          <button
            onClick={() => setIsAddModalOpen(true)}
            className="btn-primary text-xs py-2 px-3.5 flex items-center gap-1.5 shadow-2xs"
          >
            <Plus className="w-3.5 h-3.5" />
            <span>Add User</span>
          </button>
        )}
      </div>

      {/* Filter Bar */}
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          {/* Search Box */}
          <div className="relative w-64">
            <input
              type="text"
              placeholder="Search by username or tenant..."
              className="modern-input w-full text-xs py-2"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          {/* Role Filter (Strictly Admin & Tenant) */}
          <select
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
            className="filter-chip cursor-pointer outline-none font-semibold"
          >
            <option value="all">⊕ Role: All Roles</option>
            <option value="admin">Admin (Platform Control)</option>
            <option value="tenant">Tenant (Organization User)</option>
          </select>

          {/* Status Filter */}
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="filter-chip cursor-pointer outline-none"
          >
            <option value="all">⊕ Status: All</option>
            <option value="Active">Active</option>
            <option value="Suspended">Suspended</option>
          </select>

          {/* Organization Filter */}
          {isSuperAdmin && (
            <select
              value={selectedTenantFilter}
              onChange={(e) => setSelectedTenantFilter(e.target.value)}
              className="filter-chip cursor-pointer outline-none"
            >
              <option value="all">⊕ Organization: All Tenants</option>
              {tenants.map((t: Tenant) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </select>
          )}
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => usersQuery.refetch()}
            className="btn-secondary text-xs py-2 px-3 flex items-center gap-1.5 shadow-2xs"
            title="Refresh database state"
          >
            <RefreshCw className={`w-3.5 h-3.5 text-slate-500 ${usersQuery.isFetching ? "animate-spin" : ""}`} />
            <span>Sync</span>
          </button>

          <button className="btn-secondary text-xs py-2 px-3 flex items-center gap-1.5 shadow-2xs">
            <SlidersHorizontal className="w-3.5 h-3.5 text-slate-500" />
            <span>View</span>
          </button>
        </div>
      </div>

      {/* Users Table */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-2xs">
        <div className="overflow-x-auto">
          <table className="modern-table">
            <thead>
              <tr>
                <th>Username</th>
                <th>User Type / Role</th>
                <th>Linked Organization</th>
                <th>Status</th>
                <th>Last Login</th>
                <th>Created At</th>
              </tr>
            </thead>
            <tbody>
              {usersQuery.isLoading ? (
                <tr>
                  <td colSpan={6} className="text-center py-16 text-slate-400 text-xs">
                    <div className="flex flex-col items-center justify-center gap-2">
                      <RefreshCw className="w-6 h-6 text-emerald-600 animate-spin" />
                      <span className="font-semibold text-slate-600">Loading user accounts...</span>
                      <span className="text-[11px] text-slate-400">Fetching Admin and Tenant role assignments</span>
                    </div>
                  </td>
                </tr>
              ) : filteredUsers.length === 0 ? (
                <tr>
                  <td colSpan={6} className="text-center py-12 text-slate-400 text-xs">
                    No users found matching filter criteria.
                  </td>
                </tr>
              ) : (
                filteredUsers.map((u: PlatformUserItem) => (
                  <tr key={u.id} className="hover:bg-slate-50/70 transition-colors">
                    {/* Username */}
                    <td>
                      <div className="flex items-center gap-2">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-xs ${
                          u.role === "admin" ? "bg-purple-100 text-purple-800" : "bg-emerald-100 text-emerald-800"
                        }`}>
                          {u.username.slice(0, 2).toUpperCase()}
                        </div>
                        <div>
                          <span className="font-bold text-slate-900 text-xs">{u.username}</span>
                          <div className="flex items-center gap-1 text-[10px] font-mono text-slate-400">
                            <button onClick={() => copyText(u.id)} title="Copy ID" className="hover:text-slate-700">
                              <Copy className="w-2.5 h-2.5" />
                            </button>
                            <span>{u.id.slice(0, 8)}...</span>
                          </div>
                        </div>
                      </div>
                    </td>

                    {/* Role (Strictly Admin or Tenant) */}
                    <td>
                      {u.role === "admin" ? (
                        <span className="px-2.5 py-1 rounded-md text-[11px] font-extrabold bg-purple-50 text-purple-700 border border-purple-200 inline-flex items-center gap-1">
                          <ShieldAlert className="w-3 h-3 text-purple-600" />
                          Admin
                        </span>
                      ) : (
                        <span className="px-2.5 py-1 rounded-md text-[11px] font-extrabold bg-emerald-50 text-emerald-700 border border-emerald-200 inline-flex items-center gap-1">
                          <Building2 className="w-3 h-3 text-emerald-600" />
                          Tenant
                        </span>
                      )}
                    </td>

                    {/* Linked Organization */}
                    <td>
                      <span className="text-xs font-semibold text-slate-800">
                        {u.tenant_name || (u.role === "admin" ? "Global System" : "Primary Tenant")}
                      </span>
                    </td>

                    {/* Status */}
                    <td>
                      <span className={`status-pill ${u.is_active ? "status-pill--connected" : "status-pill--disconnected"}`}>
                        {u.is_active ? "Active" : "Suspended"}
                      </span>
                    </td>

                    {/* Last Login */}
                    <td className="font-mono text-xs text-slate-500">
                      {u.last_login_at ? new Date(u.last_login_at).toLocaleString() : "—"}
                    </td>

                    {/* Created At */}
                    <td className="font-mono text-xs text-slate-500">
                      {new Date(u.created_at).toLocaleDateString()}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add User Modal */}
      {isAddModalOpen && (
        <div className="modal-backdrop" onClick={() => setIsAddModalOpen(false)}>
          <div className="modal-card max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between pb-3 border-b border-slate-200 mb-3">
              <h3 className="text-base font-bold text-slate-900 flex items-center gap-2">
                <User className="w-4 h-4 text-emerald-600" />
                Create Platform User
              </h3>
              <button onClick={() => setIsAddModalOpen(false)} className="text-slate-400 hover:text-slate-700 text-xs font-bold">
                ✕
              </button>
            </div>

            <div className="space-y-3.5">
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">
                  User Type / Role *
                </label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => setNewUserRole("tenant")}
                    className={`p-3 rounded-lg border text-left transition-all ${
                      newUserRole === "tenant"
                        ? "bg-emerald-50 border-emerald-500 text-emerald-900 font-bold"
                        : "border-slate-200 text-slate-600 hover:bg-slate-50"
                    }`}
                  >
                    <div className="flex items-center gap-1.5 text-xs">
                      <Building2 className="w-4 h-4 text-emerald-600" />
                      Tenant User
                    </div>
                    <p className="text-[10px] text-slate-500 font-normal mt-0.5">Operates within an organization</p>
                  </button>

                  <button
                    type="button"
                    onClick={() => setNewUserRole("admin")}
                    className={`p-3 rounded-lg border text-left transition-all ${
                      newUserRole === "admin"
                        ? "bg-purple-50 border-purple-500 text-purple-900 font-bold"
                        : "border-slate-200 text-slate-600 hover:bg-slate-50"
                    }`}
                  >
                    <div className="flex items-center gap-1.5 text-xs">
                      <ShieldAlert className="w-4 h-4 text-purple-600" />
                      Admin
                    </div>
                    <p className="text-[10px] text-slate-500 font-normal mt-0.5">Global platform control plane</p>
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">
                  Username *
                </label>
                <input
                  type="text"
                  placeholder="e.g. jdoe_ops"
                  className="modern-input w-full text-xs font-mono"
                  value={newUsername}
                  onChange={(e) => setNewUsername(e.target.value)}
                  autoFocus
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">
                  Password *
                </label>
                <input
                  type="password"
                  placeholder="Minimum 6 characters"
                  className="modern-input w-full text-xs font-mono"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                />
              </div>

              {newUserRole === "tenant" && (
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">
                    Linked Organization / Tenant *
                  </label>
                  <select
                    value={selectedTenantId}
                    onChange={(e) => setSelectedTenantId(e.target.value)}
                    className="modern-input w-full text-xs font-semibold"
                  >
                    {tenants.map((t: Tenant) => (
                      <option key={t.id} value={t.id}>
                        {t.name} ({t.slug})
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
                <button className="btn-secondary text-xs" onClick={() => setIsAddModalOpen(false)}>
                  Cancel
                </button>
                <button
                  className="btn-primary text-xs"
                  onClick={() => createUserMutation.mutate()}
                  disabled={!newUsername.trim() || !newPassword.trim() || createUserMutation.isPending}
                >
                  {createUserMutation.isPending ? "Creating..." : "Save User Account"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
